import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Headphones } from 'lucide-react';

const Footer = () => {
  const serviceLinks = [
    { label: 'Managed IT', href: '/managed-it' },
    { label: 'Microsoft 365', href: '/microsoft-365' },
    { label: 'Cybersecurity', href: '/cybersecurity' },
    { label: 'Apple MDM', href: '/mdm' },
    { label: 'Cloud Solutions', href: '/services' },
  ];

  const quickLinks = [
    { label: 'Pricing', href: '/pricing' },
    { label: 'About Us', href: '/about' },
    { label: 'Contact', href: '/contact' },
  ];

  const clientLinks = [
    { label: 'Support Portal', href: 'https://support.microsystemsnetworking.com', external: true },
    { label: 'Referral Program', href: '/referrals', external: false },
  ];

  return (
    <footer className="bg-gray-50 border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-12 gap-8">
          {/* Company Info */}
          <div className="md:col-span-3 space-y-4">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">M</span>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-lg">Microsystems</h3>
                <p className="text-gray-600 text-sm">Networking</p>
              </div>
            </div>
            <p className="text-gray-600 text-sm leading-relaxed">
              Professional IT services for small businesses and nonprofits.
            </p>
          </div>

          {/* Services */}
          <div className="md:col-span-2">
            <h4 className="font-bold text-gray-900 mb-4">Services</h4>
            <ul className="space-y-3">
              {serviceLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.href}
                    className="text-gray-600 hover:text-blue-600 transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div className="md:col-span-2">
            <h4 className="font-bold text-gray-900 mb-4">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.href}
                    className="text-gray-600 hover:text-blue-600 transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Client Resources */}
          <div className="md:col-span-2">
            <h4 className="font-bold text-gray-900 mb-4">For Clients</h4>
            <ul className="space-y-3">
              {clientLinks.map((link, index) => (
                <li key={index}>
                  {link.external ? (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-600 hover:text-blue-600 transition-colors text-sm flex items-center"
                      data-testid="support-portal-link"
                    >
                      {link.label === 'Support Portal' && <Headphones className="w-4 h-4 mr-1.5" />}
                      {link.label}
                    </a>
                  ) : (
                    <Link
                      to={link.href}
                      className="text-gray-600 hover:text-blue-600 transition-colors text-sm"
                    >
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact - adjusted column */}
          <div className="md:col-span-3">
            <h4 className="font-bold text-gray-900 mb-4">Contact</h4>
            <ul className="space-y-4">
              <li>
                <a
                  href="tel:+17746447101"
                  className="flex items-center space-x-3 text-gray-600 hover:text-blue-600 transition-colors"
                >
                  <Phone className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <span className="text-sm">774-644-7101</span>
                </a>
              </li>
              <li>
                <a
                  href="mailto:info@microsystemsnetworking.com"
                  className="flex items-center space-x-3 text-gray-600 hover:text-blue-600 transition-colors"
                >
                  <Mail className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <span className="text-sm">info@microsystemsnetworking.com</span>
                </a>
              </li>
              <li>
                <div className="flex items-center space-x-3 text-gray-600">
                  <MapPin className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <span className="text-sm">South Shore & Greater Boston, MA</span>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-gray-200 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <p className="text-sm text-gray-600">
            © 2026 Microsystems Networking LLC. All rights reserved.
          </p>
          <div className="flex space-x-6">
            <Link to="/privacy" className="text-sm text-gray-600 hover:text-blue-600 transition-colors">
              Privacy Policy
            </Link>
            <Link to="/service-area" className="text-sm text-gray-600 hover:text-blue-600 transition-colors">
              Service Area
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
